import { createStore, del, entries, set } from "idb-keyval";
import { api } from "./api";

// Event captures made without signal live in IndexedDB until they sync.
const store = createStore("fff-event-capture", "queue");
const CHANGED = "fff-queue-changed";
let syncing = false;

const notify = () => window.dispatchEvent(new Event(CHANGED));

export const onQueueChange = (fn) => {
  window.addEventListener(CHANGED, fn);
  return () => window.removeEventListener(CHANGED, fn);
};

// No response at all (offline, DNS, timeout) — worth retrying later.
export const isNetworkError = (err) => !err?.response;

export const enqueue = async (item) => {
  const qid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  await set(qid, { ...item, qid, queued_at: new Date().toISOString() }, store);
  notify();
  return qid;
};

export const listQueue = async (eventId) => {
  const all = (await entries(store)).map(([, v]) => v).sort((a, b) => a.qid.localeCompare(b.qid));
  return eventId ? all.filter((i) => i.event_id === eventId) : all;
};

const postContact = (eventId, payload) =>
  api.post(`/admin/events/${eventId}/contacts`, payload).catch((err) => {
    // AI sometimes reads an email the server rejects — keep the rest.
    if (err?.response?.status === 422 && payload.email) {
      return api.post(`/admin/events/${eventId}/contacts`, { ...payload, email: null });
    }
    throw err;
  });

const syncItem = async (item) => {
  if (item.type === "contact") {
    await postContact(item.event_id, item.payload);
    return;
  }
  const fd = new FormData();
  fd.append("file", item.blob, "card.jpg");
  const { data } = await api.post("/admin/events/scan-card", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
  await postContact(item.event_id, {
    ...data.fields, email: data.fields.email || null, source: "card", card_image_id: data.card_image_id, needs_review: true,
  });
};

// Uploads everything waiting. Stops at the first network failure; returns how many synced.
export const syncQueue = async () => {
  if (syncing || !navigator.onLine) return 0;
  syncing = true;
  notify();
  let done = 0;
  try {
    for (const item of await listQueue()) {
      try {
        await syncItem(item);
        await del(item.qid, store);
        done += 1;
      } catch (err) {
        if (isNetworkError(err)) break;
        if (err?.response?.status === 404) await del(item.qid, store);  // event was deleted
        else await set(item.qid, { ...item, error: err?.response?.status || "error" }, store);
      }
    }
  } finally {
    syncing = false;
    notify();
  }
  return done;
};

export const isSyncing = () => syncing;
